import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Toolbar,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const COMPANY = 'KT Mountain Ranch Quarter Horses LLC';
const CONTACT_EMAIL = 'terms@ktmranch.com';
const EFFECTIVE_DATE = 'June 26, 2026';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {children}
    <Divider sx={{ mt: 3 }} />
  </Box>
);

const TermsConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {COMPANY}
          </Typography>
          <Button color="inherit" variant="outlined" onClick={() => navigate('/login')}>
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Terms and Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Effective date: {EFFECTIVE_DATE}
        </Typography>

        <Typography variant="body1" paragraph>
          These Terms and Conditions ("Terms") govern your use of the KTM Ranch Breeding platform
          operated by {COMPANY} ("we," "our," or "us"). By accessing or using the platform, you
          agree to be bound by these Terms.
        </Typography>

        <Section title="1. Acceptance of Terms">
          <Typography variant="body1" paragraph>
            By creating an account or using the platform, you confirm that you have read,
            understood, and agree to these Terms. If you do not agree, you may not use the
            platform.
          </Typography>
        </Section>

        <Section title="2. Description of Service">
          <Typography variant="body1" paragraph>
            The KTM Ranch Breeding platform is an equestrian breeding management tool designed
            for ranch operators. It provides features for tracking horses, breeding records,
            expenses, income, appointments, and related ranch operations. Access is granted on
            an invitation or approved-registration basis.
          </Typography>
        </Section>

        <Section title="3. Account Responsibilities">
          <Typography variant="body1" paragraph>
            You are responsible for:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0 }}>
            <Typography component="li" variant="body1">Maintaining the confidentiality of your login credentials</Typography>
            <Typography component="li" variant="body1">All activity that occurs under your account</Typography>
            <Typography component="li" variant="body1">Notifying us immediately of any unauthorized access at{' '}
              <Box component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{ color: 'primary.main' }}>
                {CONTACT_EMAIL}
              </Box>
            </Typography>
            <Typography component="li" variant="body1">Ensuring the accuracy of information you enter into the platform</Typography>
          </Box>
        </Section>

        <Section title="4. Acceptable Use">
          <Typography variant="body1" paragraph>
            You agree to use the platform only for lawful purposes and in accordance with these
            Terms. You may not:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0 }}>
            <Typography component="li" variant="body1">Use the platform for any unlawful or fraudulent purpose</Typography>
            <Typography component="li" variant="body1">Attempt to gain unauthorized access to any part of the platform or its infrastructure</Typography>
            <Typography component="li" variant="body1">Reverse engineer, decompile, or disassemble the platform</Typography>
            <Typography component="li" variant="body1">Introduce malicious code, viruses, or other harmful material</Typography>
            <Typography component="li" variant="body1">Use the platform in a way that could damage, disable, or impair its operation</Typography>
          </Box>
        </Section>

        <Section title="5. Data Ownership">
          <Typography variant="body1" paragraph>
            You retain full ownership of all ranch data you enter into the platform, including
            horse records, breeding data, financial records, and uploaded files. We do not claim
            any ownership rights over your data.
          </Typography>
          <Typography variant="body1" paragraph>
            You grant us a limited license to store, process, and display your data solely for
            the purpose of providing the platform's functionality.
          </Typography>
        </Section>

        <Section title="6. Limitation of Liability">
          <Typography variant="body1" paragraph>
            The platform is provided "as is" without warranties of any kind, express or implied.
            We do not warrant that the platform will be uninterrupted, error-free, or completely
            secure.
          </Typography>
          <Typography variant="body1" paragraph>
            To the fullest extent permitted by law, {COMPANY} shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages, including but not
            limited to loss of data, loss of profits, or damages resulting from operational
            decisions made using information provided by the platform (including AI-generated
            suggestions).
          </Typography>
        </Section>

        <Section title="7. Termination">
          <Typography variant="body1" paragraph>
            We reserve the right to suspend or terminate your access to the platform at our
            discretion, with or without notice, for conduct that violates these Terms or that
            we determine to be harmful to other users, us, or third parties.
          </Typography>
        </Section>

        <Section title="8. Changes to Terms">
          <Typography variant="body1" paragraph>
            We may update these Terms from time to time. We will notify users of material changes
            by updating the effective date. Continued use of the platform after changes are posted
            constitutes your acceptance of the revised Terms.
          </Typography>
        </Section>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body1">
            <strong>Contact:</strong>{' '}
            <Box component="a" href={`mailto:${CONTACT_EMAIL}`} sx={{ color: 'primary.main' }}>
              {CONTACT_EMAIL}
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {COMPANY}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsConditionsPage;
